import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '@/components/ui/Button'
import { userApi } from '@/services/api'
import { useAuthStore } from '@/store/auth'
import { loadSituations, type Situation } from '@/services/content'
import Icon from '@/components/ui/Icon'
import styles from './SituationPicker.module.css'

export default function SituationPicker() {
  const [situations, setSituations] = useState<Situation[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingSituations, setLoadingSituations] = useState(true)
  const navigate = useNavigate()
  const setUser = useAuthStore((state) => state.setUser)

  useEffect(() => {
    loadSituations()
      .then(setSituations)
      .finally(() => setLoadingSituations(false))
  }, [])

  const toggleSituation = (id: string) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((s) => s !== id)
        : [...prev, id]
    )
  }

  const handleContinue = async () => {
    setLoading(true)
    try {
      if (selected.length > 0) {
        await userApi.updateMe({ situations_selected: selected })
      }
      const userResponse = await userApi.getMe()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setUser(userResponse.data as any)
      navigate('/')
    } catch {
      // Navigate anyway on error
      navigate('/')
    } finally {
      setLoading(false)
    }
  }

  const handleSkip = () => {
    navigate('/')
  }

  return (
    <div className={styles.container}>
      <button className={styles.skipButton} onClick={handleSkip}>
        Skip for now
      </button>

      <div className={styles.content}>
        <h2 className={styles.title}>
          What sounds like your child?
        </h2>
        <p className={styles.subtitle}>
          Pick all that apply. This helps us show you the right activities.
        </p>

        {loadingSituations ? (
          <div className={styles.loading}>Loading...</div>
        ) : (
          <div className={styles.grid}>
            {situations.map((situation) => (
              <button
                key={situation.id}
                className={`${styles.card} ${selected.includes(situation.id) ? styles.cardSelected : ''}`}
                onClick={() => toggleSituation(situation.id)}
              >
                <span className={styles.icon}><Icon name={situation.icon} size={32} /></span>
                <span className={styles.label}>{situation.title}</span>
                {selected.includes(situation.id) && (
                  <span className={styles.check}>✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <Button
          onClick={handleContinue}
          fullWidth
          size="lg"
          loading={loading}
        >
          {selected.length > 0 ? `Continue (${selected.length} selected)` : 'Continue'}
        </Button>
      </div>
    </div>
  )
}
